/**
 * The one way the app shows an OS notification (2026-11-01). Whoever decides
 * that something deserves one (a DM, a mention, a reply, a call, a poke, a
 * friend request, a channel whose notification level allows it) calls
 * `notify()`; this file decides only how and whether it can be delivered:
 *
 * - Never for your own messages, for someone you ignore or blocked, or while
 *   you are on Do Not Disturb.
 * - Never for the conversation you are looking at while the window has the
 *   focus (Discord's rule). Calls, pokes and anything without a conversation
 *   show in the app instead while it has the focus.
 * - Bursts collapse: one toast per conversation per `BURST_MS`; what arrives
 *   meanwhile updates it once the window ends, with the count ("Ali (3)").
 * - "Show message preview" off: the toast says "New message from Ali".
 * - The sound (the message sound's switch, "Notification sounds") plays with
 *   it; calls and pokes bring their own (`silent`).
 * - DMs, calls and pokes flash the taskbar (desktop) or the tab's title (web),
 *   with "Flash taskbar".
 * - The pop-up itself needs "Enable desktop notifications" (the account's):
 *   the desktop app's native toast (bridge `notify`), else the browser's (or
 *   WebView2's) `Notification`. Clicking it opens the route.
 */
import { RelationshipType, type Snowflake } from '@neosocial/protocol';
import { tr } from '../i18n';
import { desktopBridge } from '../lib/config';
import { desktopWindow } from '../lib/desktopGlue';
import { play } from '../sound/sounds';
import { useData } from '../store/data';
import { isIgnored } from '../store/um';
import { appIconPng } from './badge';
import { offerPermission } from './permission';
import { notifyPrefs } from './prefs';
import {
  encodeRoute,
  openRoute,
  routeKey,
  shownKey,
  windowFocused,
  type NotifyRoute,
} from './route';
import { flashTitle } from './title';

export type NotifyKind =
  | 'dm'
  | 'mention'
  | 'reply'
  /** A channel message that the channel's notification level allows. */
  | 'message'
  | 'call'
  | 'poke'
  | 'friend_request'
  /** A scheduled event you are interested in started (events/store.ts). */
  | 'event';

export interface NotifyInput {
  kind: NotifyKind;
  /** "Ali", "Ali in #general". */
  title: string;
  /** The message text (hidden when "Show message preview" is off). */
  body?: string;
  /** Who it is from: your own, ignored and blocked people never notify. */
  from?: Snowflake | null;
  /** The name for "New message from {who}" (defaults to the title). */
  who?: string;
  /** The web's icon (the sender's avatar); the app's icon otherwise. */
  icon?: string | null;
  /** What a click opens; also what "in view" compares. */
  route?: NotifyRoute | null;
  /** Groups the toasts (one per tag); defaults to the route's conversation. */
  tag?: string;
  /** No sound from here (the caller plays its own: the poke's buzz, a ringtone). */
  silent?: boolean;
  /** Stays up until dismissed (web) or for longer (desktop); never collapsed. */
  sticky?: boolean;
}

export type NotifyResult =
  | 'shown'
  /** Folded into the conversation's next toast. */
  | 'collapsed'
  | 'own'
  | 'blocked'
  | 'dnd'
  | 'in_view'
  /** Sound and flash only: the pop-up is off, or not allowed by the browser. */
  | 'off';

/** One toast per conversation per this long. */
export const BURST_MS = 5000;
/** A conversation quiet this long starts counting again. */
export const QUIET_MS = 120_000;

/** Taskbar flash: 1 Critical (until focused), 2 Informational. */
const FLASH: Partial<Record<NotifyKind, 1 | 2>> = { call: 1, poke: 1, dm: 2 };
const MESSAGES: ReadonlySet<NotifyKind> = new Set(['dm', 'mention', 'reply', 'message']);

/** A tag the shell accepts (1 to 64 of A-Z a-z 0-9 : _ - .). */
export function cleanTag(tag: string): string {
  return tag.replace(/[^A-Za-z0-9:_.-]/g, '_').slice(0, 64) || 'neosocial';
}

function tagOf(n: NotifyInput): string {
  return cleanTag(n.tag ?? (n.route ? routeKey(n.route) : n.kind));
}

/** Why `n` must not show now, or null. */
export function skipReason(
  n: NotifyInput,
): Exclude<NotifyResult, 'shown' | 'collapsed' | 'off'> | null {
  const d = useData.getState();
  if (n.from && d.me && n.from === d.me.id) return 'own';
  if (n.from && (isIgnored(n.from) || d.relationships[n.from]?.type === RelationshipType.BLOCKED))
    return 'blocked';
  if (d.settings?.status === 'dnd') return 'dnd';
  if (windowFocused()) {
    if (!n.route || n.kind === 'call' || n.kind === 'poke' || n.kind === 'event') return 'in_view';
    if (shownKey() === routeKey(n.route)) return 'in_view';
  }
  return null;
}

/** The toast's texts for `count` messages, with the preview rule. */
export function noticeText(n: NotifyInput, count = 1): { title: string; body: string } {
  const { t } = tr();
  const title = count > 1 ? t('nt.count', { title: n.title, n: count }) : n.title;
  if (notifyPrefs().preview || !MESSAGES.has(n.kind)) return { title, body: n.body ?? '' };
  const who = n.who ?? n.title;
  return {
    title,
    body: count > 1 ? t('nt.privateMany', { n: count, who }) : t('nt.private', { who }),
  };
}

/* ------------------------------------------------------------- bursts */

interface Burst {
  /** Messages since the conversation was last seen (or went quiet). */
  count: number;
  /** No other toast before this. */
  until: number;
  last: number;
  /** The newest message waiting for the window to end. */
  pending: NotifyInput | null;
  timer: ReturnType<typeof setTimeout> | null;
}

const bursts = new Map<string, Burst>();

/** The count to show now, or null when `n` waits for the window to end. */
function admit(tag: string, n: NotifyInput): number | null {
  const now = Date.now();
  let b = bursts.get(tag);
  if (b && !b.timer && now - b.last > QUIET_MS) {
    bursts.delete(tag);
    b = undefined;
  }
  if (!b) {
    bursts.set(tag, { count: 1, until: now + BURST_MS, last: now, pending: null, timer: null });
    return 1;
  }
  b.count += 1;
  b.last = now;
  if (now >= b.until && !b.timer) {
    b.until = now + BURST_MS;
    return b.count;
  }
  b.pending = n;
  b.timer ??= setTimeout(() => flush(tag), Math.max(0, b.until - now));
  return null;
}

function flush(tag: string): void {
  const b = bursts.get(tag);
  if (!b) return;
  b.timer = null;
  const n = b.pending;
  b.pending = null;
  if (!n) return;
  const skip = skipReason(n);
  if (skip) {
    if (skip === 'in_view') bursts.delete(tag);
    return;
  }
  b.until = Date.now() + BURST_MS;
  deliver(n, b.count, tag);
}

/* ------------------------------------------------------------- delivery */

/** Web notifications on screen, by tag. */
const open = new Map<string, Notification>();
/** Tags with a toast out (desktop or web), so seeing the conversation clears it. */
const out = new Set<string>();

function flash(n: NotifyInput): void {
  const level = FLASH[n.kind];
  if (!level || !notifyPrefs().flash || typeof document === 'undefined') return;
  const win = desktopBridge() ? desktopWindow() : null;
  if (win) {
    if (!document.hasFocus()) void win.requestUserAttention?.(level).catch(() => {});
  } else flashTitle(n.title);
}

function webNotify(tag: string, title: string, body: string, n: NotifyInput): boolean {
  if (typeof Notification === 'undefined') return false;
  const show = () => {
    try {
      const icon = n.icon ?? appIconPng();
      const x = new Notification(title, {
        body,
        tag,
        // the app plays its own sound ("Notification sounds")
        silent: true,
        ...(icon ? { icon } : {}),
        ...(n.sticky ? { requireInteraction: true } : {}),
      });
      x.onclick = () => {
        x.close();
        openRoute(n.route ?? null);
      };
      x.onclose = () => {
        if (open.get(tag) === x) open.delete(tag);
      };
      open.set(tag, x);
      out.add(tag);
    } catch {
      /* some browsers refuse outside a service worker */
    }
  };
  if (Notification.permission === 'granted') {
    show();
    return true;
  }
  if (Notification.permission === 'default') {
    // WebView2 (an older desktop shell) asks once and remembers; the web asks through the app's bar first
    if (desktopBridge())
      void Notification.requestPermission()
        .then((r) => r === 'granted' && show())
        .catch(() => {});
    else offerPermission();
  }
  return false;
}

function deliver(n: NotifyInput, count: number, tag: string): NotifyResult {
  if (!n.silent) play('message');
  flash(n);
  if (!useData.getState().settings?.desktop_notifications) return 'off';
  const { title, body } = noticeText(n, count);
  const bridge = desktopBridge();
  if (bridge?.notify) {
    out.add(tag);
    const route = n.route ? encodeRoute(n.route) : null;
    void bridge
      .notify({ tag, title, body, route, sticky: !!n.sticky })
      // no native toasts on this OS: the webview's own
      .catch(() => webNotify(tag, title, body, n));
    return 'shown';
  }
  return webNotify(tag, title, body, n) ? 'shown' : 'off';
}

/**
 * Shows an OS notification for something that deserves one, if it can be
 * delivered (the rules at the top of this file). The single entry point.
 */
export function notify(n: NotifyInput): NotifyResult {
  const tag = tagOf(n);
  const skip = skipReason(n);
  if (skip) {
    if (skip === 'in_view') forget(tag);
    return skip;
  }
  const once = n.sticky || n.kind === 'call' || n.kind === 'poke' || n.kind === 'event';
  const count = once ? 1 : admit(tag, n);
  if (count === null) return 'collapsed';
  return deliver(n, count, tag);
}

function forget(tag: string): void {
  const b = bursts.get(tag);
  if (b?.timer) clearTimeout(b.timer);
  bursts.delete(tag);
}

/** Closes one notification (a poke's, when its card closes; a call's, when it ends). */
export function closeNotification(tag: string): void {
  const t = cleanTag(tag);
  forget(t);
  open.get(t)?.close();
  open.delete(t);
  if (out.delete(t))
    void desktopBridge()
      ?.clearNotifications?.(t)
      .catch(() => {});
}

/** You are looking at this conversation: its toasts go and its count starts again. */
export function seenConversation(key: string): void {
  const t = cleanTag(key);
  if (bursts.has(t) || out.has(t)) closeNotification(t);
}

/**
 * An incoming DM call (for the call screen): rings in the OS while the app
 * is in the background, flashes the taskbar until focused. The ringtone is
 * the caller's own (`silent`). `endCallNotification` takes it away.
 */
export function notifyIncomingCall(c: {
  channelId: Snowflake;
  /** The caller (a DM's other person); null for a group's call. */
  from: Snowflake | null;
  /** The DM's or the group's name. */
  who: string;
  icon?: string | null;
  /** Defaults to "Incoming call". */
  body?: string;
}): NotifyResult {
  return notify({
    kind: 'call',
    title: c.who,
    body: c.body ?? tr().t('nt.call'),
    from: c.from,
    icon: c.icon,
    route: { to: 'dm', channelId: c.channelId },
    tag: 'call:' + c.channelId,
    silent: true,
    sticky: true,
  });
}

export function endCallNotification(channelId: Snowflake): void {
  closeNotification('call:' + channelId);
}

/** Tests start clean. */
export function resetNotify(): void {
  for (const b of bursts.values()) if (b.timer) clearTimeout(b.timer);
  bursts.clear();
  open.clear();
  out.clear();
}
